import type { FlowRunDto } from '@reelplicate/api-client/model';
import { Badge, Tooltip } from '@reelplicate/ui';

import { runKind } from '../../flow-status';

/**
 * The shown run's state beside the flow's status (Figma "Running · 14 of 15"):
 * progress while it runs, what it's waiting for, or how it ended.
 */
export function RunBadge({ run }: { run: FlowRunDto | undefined }) {
  if (!run) return null;
  const kind = runKind(run);
  const done = run.nodes.reduce((sum, node) => sum + node.tasksDone, 0);
  const total = run.nodes.reduce((sum, node) => sum + node.tasksTotal, 0);
  const waiting = run.nodes.reduce(
    (sum, node) =>
      sum + (node.status === 'WAITING' ? node.tasksTotal - node.tasksDone : 0),
    0,
  );
  switch (run.status) {
    case 'QUEUED':
    case 'RUNNING':
      return (
        <Badge tone="info" dot>
          {kind} · {done} of {total}
        </Badge>
      );
    case 'WAITING':
      return (
        <Badge tone="warning" dot>
          {kind} · {waiting} waiting for review
        </Badge>
      );
    case 'SLEEPING':
      return (
        <Badge tone="info" dot>
          {kind} · measuring
        </Badge>
      );
    case 'SUCCEEDED':
      return (
        <Badge tone="success" dot>
          {kind} done{run.held.length ? ` · ${run.held.length} held` : ''}
        </Badge>
      );
    case 'FAILED':
      return (
        <Tooltip content={run.error ?? 'The run failed'}>
          <span
            tabIndex={0}
            className="focus-visible:shadow-focus-ring rounded-xs outline-none"
          >
            <Badge tone="danger" dot>
              {kind} failed
            </Badge>
          </span>
        </Tooltip>
      );
    case 'CANCELLED':
      return <Badge dot>{kind} stopped</Badge>;
  }
}
